<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import CCard from '@lib/components/CCard/CCard.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'

import useThemeVars from '@lib/composables/useThemeVars'
import { displayBytesWithMUnit } from '@lib/util/number'
import type { DeviceFile } from '@lib/types/files'

const props = defineProps<{
  modelValue: File[] | DeviceFile[]
}>()

const emit = defineEmits<{
  (e: 'addFiles'): void
  (e: 'update:modelValue', files: File[] | DeviceFile[]): void
}>()

const files = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const { t } = useI18n()
const themeVars = useThemeVars()

function onRemoveFile(file: File | DeviceFile) {
  files.value = files.value.filter((f) => f.name !== file.name) as File[] | DeviceFile[]
}
</script>

<template>
  <CCard :bordered="true">
    <div class="max-h-[200px] overflow-y-auto">
      <div v-for="file of files" :key="file.name" class="flex justify-between items-center gap-5 h-[50px] px-4 min-w-0">
        <div v-tooltip="file.name" class="font-bold truncate flex-grow overflow-hidden">{{ file.name }}</div>
        <div class="flex items-center gap-5 flex-shrink-0">
          <div class="whitespace-nowrap">
            {{ displayBytesWithMUnit(file.size) }}
          </div>
          <CButton circle @click="onRemoveFile(file)">
            <CIcon fa-type="fas" icon="close" size="large" />
          </CButton>
        </div>
      </div>
    </div>

    <div class="flex justify-center mt-4">
      <CButton icon-placement="left" secondary @click="emit('addFiles')">
        <template #icon>
          <CIcon icon="upload" size="16px" />
        </template>
        {{ t('canvasUI.browserApp.uploadFilesModal.addMore').toUpperCase() }}
      </CButton>
    </div>
  </CCard>
</template>

<style lang="scss" scoped>
.upload-files-model-file-list {
  &__row {
    border-bottom: 1px solid v-bind('themeVars.primary6');
    &-file-size {
      font-size: 12px;
      color: v-bind('themeVars.text2');
    }
  }
}
</style>
